import { useCallback, useEffect, useRef, useState } from "react";
import { ShieldAlert } from "lucide-react";

import Typography from "@/components/ui/typography";

import {
  ContentBlock,
  getThreadMessages,
  streamMessage,
} from "../api/messages";
import { AgentSuggestions } from "./AgentSuggestions";
import { ChatHeader } from "./ChatHeader";
import { ChatInput } from "./ChatInput";
import { Message } from "./ChatMessage";
import { ChatMessages } from "./ChatMessages";
import { EnhancedChatInput } from "./EnhancedChatInput";

interface ChatContainerProps {
  userName?: string;
  threadId: string | null;
  threadTitle?: string | null;
  isOnboarded?: boolean;
  onCreateThread?: () => Promise<string>;
  onThreadCreated?: (threadId: string) => void;
  onMessageSent?: () => void;
  initialPrompt?: string;
}

export const ChatContainer = ({
  userName,
  threadId,
  threadTitle,
  isOnboarded = false,
  onCreateThread,
  onThreadCreated,
  onMessageSent,
  initialPrompt,
}: ChatContainerProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasReceivedTokens, setHasReceivedTokens] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [pendingAgentPrompt, setPendingAgentPrompt] = useState<string>(
    initialPrompt || "",
  );
  const isStreamingRef = useRef(false);
  const hasReceivedTokensRef = useRef(false);
  const enhancedInputRef = useRef<HTMLTextAreaElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  // Load messages when thread changes
  useEffect(() => {
    const loadMessages = async () => {
      if (!threadId) {
        // Only clear messages if we're switching away from a thread
        if (currentThreadId !== null) {
          setMessages([]);
        }
        setCurrentThreadId(null);
        return;
      }

      // Only load if thread actually changed
      if (threadId === currentThreadId) {
        return;
      }

      // Don't reload if we're currently sending/streaming a message
      if (isStreamingRef.current) {
        console.log("Skipping message load - currently streaming");
        setCurrentThreadId(threadId);
        return;
      }

      // Load messages from backend
      console.log("Loading messages for thread:", threadId);
      setLoadingMessages(true);
      try {
        const threadMessages = await getThreadMessages(threadId);
        console.log("Loaded messages:", threadMessages.length);
        setMessages(threadMessages);
        setCurrentThreadId(threadId);
      } catch (error) {
        console.error("Failed to load messages:", error);
        setMessages([]);
        setCurrentThreadId(threadId);
      } finally {
        setLoadingMessages(false);
      }
    };

    loadMessages();
     
  }, [threadId]); // Only depend on threadId prop, not currentThreadId state


  const handleSend = useCallback(
    async (text: string, attachments?: ContentBlock[]) => {
      // Build display content for user message
      const attachmentCount = attachments?.length || 0;

      // Add user message
      const userMessage: Message = {
        id: Date.now().toString(),
        content: text,
        role: "user",
        timestamp: new Date(),
        attachmentCount: attachmentCount > 0 ? attachmentCount : undefined,
      };

      // Create assistant message placeholder IMMEDIATELY
      const assistantMessageId = (Date.now() + 1).toString();
      const assistantMessage: Message = {
        id: assistantMessageId,
        content: "",
        thinking: undefined,
        role: "assistant",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setIsLoading(true);
      setHasReceivedTokens(false);
      hasReceivedTokensRef.current = false;
      isStreamingRef.current = true;

      try {
        // If no thread, create one first
        let activeThread = threadId;
        if (!activeThread && onCreateThread) {
          console.log("No active thread, creating new one...");
          activeThread = await onCreateThread();
          console.log("Created thread:", activeThread);

          // Set internal thread ID immediately to prevent reload
          setCurrentThreadId(activeThread);

          // Notify parent that thread was created
          if (onThreadCreated) {
            onThreadCreated(activeThread);
          }
        }

        if (!activeThread) {
          throw new Error("No thread ID available");
        }

        // Stream the response token by token
        let fullContent = "";
        let fullThinking = "";

        for await (const chunk of streamMessage({
          threadId: activeThread,
          message: text,
          content: attachments,
        })) {
          if (chunk.type === "token") {
            // Mark that we've received tokens (use ref to track per-session)
            // Add a small delay to ensure "Sending request" is visible
            if (
              !hasReceivedTokensRef.current &&
              (chunk.content || chunk.thinking)
            ) {
              hasReceivedTokensRef.current = true;
              // Delay state update by 300ms to ensure "Sending request" is visible
              setTimeout(() => {
                setHasReceivedTokens(true);
              }, 300);
            }

            // Accumulate content tokens as they arrive
            if (chunk.content) {
              fullContent += chunk.content;
            }

            // Accumulate thinking tokens as they arrive
            if (chunk.thinking) {
              fullThinking += chunk.thinking;
            }

            // Update message with both content and thinking
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessageId
                  ? {
                      ...msg,
                      content: fullContent,
                      thinking: fullThinking || undefined,
                    }
                  : msg,
              ),
            );
          } else if (chunk.type === "error") {
            console.error("Stream error:", chunk.error);
            throw new Error(chunk.error || "Stream error");
          } else if (chunk.type === "done") {
            break;
          }
        }

        // Notify parent that a message was sent successfully (to refresh threads list)
        if (onMessageSent) {
          onMessageSent();
        }
      } catch (error) {
        console.error("Failed to send message:", error);
        // Remove the failed assistant message
        setMessages((prev) =>
          prev.filter((msg) => msg.role === "user" || msg.content !== ""),
        );
      } finally {
        setIsLoading(false);
        setHasReceivedTokens(false);
        hasReceivedTokensRef.current = false;
        isStreamingRef.current = false;
      }
    },
    [threadId, onCreateThread, onThreadCreated, onMessageSent],
  );

  const handleAgentSelect = (prompt: string) => {
    // Auto-send the agent's trigger message immediately
    handleSend(prompt);
  };

  const handleEnhancedSend = (text: string, attachments?: ContentBlock[]) => {
    setPendingAgentPrompt("");
    handleSend(text, attachments);
  };

  const handleChatInputSend = (text: string, attachments?: ContentBlock[]) => {
    setPendingAgentPrompt("");
    handleSend(text, attachments);
  };

  // Auto-send when initialPrompt is provided (coming from agent library)
  useEffect(() => {
    if (initialPrompt) {
      // Auto-send the agent's trigger message immediately
      handleSend(initialPrompt);
    }
    // Only run when initialPrompt changes, not on every handleSend reference change
     
  }, [initialPrompt]);

  const hasMessages = messages.length > 0;

  // Beta warning component (reused in both layouts)
  const BetaWarning = () => (
    <div className="flex gap-1">
      <ShieldAlert className="w-5 h-5 text-sds-neutral-80 mt-0.5 flex-shrink-0" />
      <div className="flex items-center gap-1 flex-wrap">
        <Typography size="l4" className="text-sds-neutral-80">
          Stacey is still in beta.
        </Typography>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-sds-background-light overflow-hidden">
      {hasMessages || loadingMessages ? (
        // Has messages layout - header at top, messages scroll, input at bottom
        <>
          <div className="px-4 py-4 pb-4 md:p-6 md:pt-24 md:pb-4 flex-shrink-0 bg-sds-background-light">
            <ChatHeader
              userName={userName}
              hasMessages={hasMessages}
              threadTitle={threadTitle}
            />
          </div>

          <div className="flex-1 overflow-hidden min-h-0">
            {loadingMessages ? (
              <div className="flex items-center justify-center h-full">
                <Typography size="b2" className="text-sds-neutral-60">
                  Loading messages...
                </Typography>
              </div>
            ) : (
              <ChatMessages
                messages={messages}
                isLoading={isLoading}
                hasReceivedTokens={hasReceivedTokens}
              />
            )}
          </div>

          <div className="p-4 md:p-6 pt-2 flex flex-col gap-3 flex-shrink-0">
            <ChatInput
              onSend={handleChatInputSend}
              disabled={isLoading}
              placeholder="What can I help you with?"
              initialValue={pendingAgentPrompt}
              inputRef={chatInputRef}
            />
            <BetaWarning />
          </div>
        </>
      ) : (
        // Empty state - header at top, spacer, input at bottom
        <>
          {/* Header at TOP */}
          <div className="px-4 pt-6 pb-3 md:p-6 md:pt-24 flex-shrink-0">
            <ChatHeader
              userName={userName}
              hasMessages={false}
              threadTitle={threadTitle}
            />
          </div>

          {/* Spacer pushes input area down on mobile */}
          <div className="flex-1 md:hidden" />
          <div className="hidden md:block md:flex-1" />

          {/* Input area with more bottom padding */}
          <div className="p-4 pb-6 md:p-6 pt-2 flex flex-col gap-3 flex-shrink-0">
            <div>
              <EnhancedChatInput
                onSend={handleEnhancedSend}
                disabled={isLoading}
                initialValue={pendingAgentPrompt}
                inputRef={enhancedInputRef}
              />
              <AgentSuggestions
                onAgentSelect={handleAgentSelect}
                isOnboarded={isOnboarded}
              />
            </div>
            <BetaWarning />
          </div>
        </>
      )}
    </div>
  );
};
